<script lang="ts">
  import type { Source } from '../ws';
  import SourcePreviewCard from './SourcePreviewCard.svelte';

  let {
    sources,
    palette,
    title = 'Fuentes',
    onClose,
  } = $props<{
    sources: Source[];
    palette: {
      textPrimary: string;
      textSecondary: string;
      bgChatInput: string;
      accent: string;
    };
    title?: string;
    onClose?: () => void;
  }>();

  let query = $state('');

  let citedSources = $derived(sources.filter((source: Source) => source.cited));
  let consultedSources = $derived(sources.filter((source: Source) => !source.cited));

  function matchesQuery(source: Source, value: string): boolean {
    const needle = value.trim().toLowerCase();
    if (!needle) return true;
    return (
      (source.url || '').toLowerCase().includes(needle) ||
      (source.title || '').toLowerCase().includes(needle) ||
      (source.domain || '').toLowerCase().includes(needle)
    );
  }

  let filteredCited = $derived(
    citedSources.filter((source: Source) => matchesQuery(source, query))
  );
  let filteredConsulted = $derived(
    consultedSources.filter((source: Source) => matchesQuery(source, query))
  );
  let noSourceMatches = $derived(
    query.trim() !== '' && filteredCited.length + filteredConsulted.length === 0
  );
</script>

<aside
  class="sources-panel"
  style="background: {palette.bgChatInput}; color: {palette.textPrimary};"
  aria-label="Panel de fuentes"
>
  <header class="sources-header">
    <div class="sources-title">
      <i class="bi bi-link-45deg sources-heading-icon" style="color: {palette.accent};"></i>
      <div class="sources-heading">
        <span class="sources-heading-text">{title}</span>
        <span class="sources-count" style="color: {palette.textSecondary};">
          {sources.length} {sources.length === 1 ? 'fuente' : 'fuentes'}
        </span>
      </div>
    </div>
    {#if onClose}
      <button
        type="button"
        class="sources-close"
        onclick={onClose}
        aria-label="Cerrar panel de fuentes"
        title="Cerrar"
        style="color: {palette.textSecondary};"
      >
        <i class="bi bi-x-lg"></i>
      </button>
    {/if}
  </header>

  <div class="sources-search">
    <i class="bi bi-search sources-search-icon"></i>
    <input
      type="text"
      bind:value={query}
      placeholder="Buscar fuentes…"
      aria-label="Buscar fuentes"
    />
    {#if query}
      <button
        type="button"
        class="sources-clear"
        onclick={() => (query = '')}
        aria-label="Limpiar búsqueda"
        title="Limpiar búsqueda"
      >
        <i class="bi bi-x-circle-fill"></i>
      </button>
    {/if}
  </div>

  <div class="sources-scroll">
    {#if sources.length === 0}
      <div class="sources-empty" style="color: {palette.textSecondary};">
        <i class="bi bi-link-45deg"></i>
        <span>Esta respuesta no tiene fuentes asociadas.</span>
      </div>
    {:else if noSourceMatches}
      <div class="sources-empty" style="color: {palette.textSecondary};">
        <i class="bi bi-search"></i>
        <span>Sin coincidencias para “{query}”.</span>
      </div>
    {:else}
      {#if filteredCited.length > 0}
        <div class="sources-section-title" style="color: {palette.textSecondary};">
          <span>🔗 Citadas en la respuesta</span>
          <span class="sources-section-count">{filteredCited.length}</span>
        </div>
        <div class="sources-list">
          {#each filteredCited as source (source.url)}
            <SourcePreviewCard {source} {palette} />
          {/each}
        </div>
      {/if}

      {#if filteredConsulted.length > 0}
        <div class="sources-section-title" style="color: {palette.textSecondary};">
          <span>⚙️ Otras fuentes encontradas</span>
          <span class="sources-section-count">{filteredConsulted.length}</span>
        </div>
        <div class="sources-list">
          {#each filteredConsulted as source (source.url)}
            <SourcePreviewCard {source} {palette} />
          {/each}
        </div>
      {/if}
    {/if}
  </div>
</aside>

<style>
  .sources-panel {
    flex: 0 0 clamp(300px, 33%, 420px);
    min-height: 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-left: 1px solid rgba(128, 128, 128, 0.16);
    box-shadow: -4px 0 12px rgba(0, 0, 0, 0.04);
  }

  .sources-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 12px 12px 8px;
    flex: 0 0 auto;
  }

  .sources-title {
    display: flex;
    align-items: center;
    gap: 9px;
    min-width: 0;
  }

  .sources-heading-icon {
    flex-shrink: 0;
    font-size: 16px;
  }

  .sources-heading {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .sources-heading-text {
    overflow: hidden;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .sources-count {
    font-size: 10.5px;
    opacity: 0.8;
  }

  .sources-close,
  .sources-clear {
    display: grid;
    place-items: center;
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
  }

  .sources-close {
    flex-shrink: 0;
    padding: 4px;
    border-radius: 6px;
    font-size: 14px;
    opacity: 0.65;
    transition: opacity 150ms ease, background-color 150ms ease;
  }

  .sources-close:hover {
    opacity: 1;
    background: rgba(128, 128, 128, 0.1);
  }

  .sources-search {
    position: relative;
    display: flex;
    align-items: center;
    padding: 0 12px 8px;
    flex: 0 0 auto;
  }

  .sources-search input {
    width: 100%;
    padding: 6px 28px 6px 27px;
    border: 1px solid rgba(128, 128, 128, 0.22);
    border-radius: 8px;
    background: rgba(128, 128, 128, 0.06);
    color: inherit;
    font-size: 12px;
    transition: border-color 150ms ease;
  }

  .sources-search input:focus {
    border-color: rgba(128, 128, 128, 0.45);
  }

  .sources-search input::placeholder {
    opacity: 0.55;
  }

  .sources-search-icon {
    position: absolute;
    left: 20px;
    font-size: 11px;
    opacity: 0.55;
    pointer-events: none;
  }

  .sources-clear {
    position: absolute;
    right: 18px;
    padding: 3px;
    border-radius: 50%;
    font-size: 11px;
    opacity: 0.5;
  }

  .sources-clear:hover {
    opacity: 1;
  }

  .sources-scroll {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    overflow-y: auto;
    padding: 2px 12px 12px;
  }

  .sources-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 24px 12px;
    font-size: 11.5px;
    line-height: 1.45;
    text-align: center;
  }

  .sources-empty > i {
    font-size: 22px;
    opacity: 0.45;
  }

  .sources-section-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    margin: 6px 4px 2px;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .sources-section-count {
    font-size: 9.5px;
    font-weight: 700;
    opacity: 0.65;
  }

  .sources-list {
    display: flex;
    flex-direction: column;
    gap: 7px;
  }

  @media (max-width: 768px) {
    .sources-panel {
      position: absolute;
      inset: 0;
      z-index: 40;
      width: 100%;
      border-left: 0;
      box-shadow: none;
    }
  }
</style>
